import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React from "react";
import { buildAppLink } from "../../utils/landingRedirect";

interface SignUpPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolName: string;
}

/**
 * Modal shown to prompt users to sign up when they interact with a free tool.
 * This is a lead generation modal - it doesn't process any AI content.
 */
const SignUpPromptModal: React.FC<SignUpPromptModalProps> = ({
  isOpen,
  onClose,
  toolName,
}) => {
  const benefits = [
    { icon: "lucide:save", text: "Save unlimited summaries & notes" },
    { icon: "lucide:search", text: "AI-powered search across all your content" },
    { icon: "lucide:smartphone", text: "Access on mobile, web & Chrome extension" },
    { icon: "lucide:zap", text: "Faster processing with premium AI models" },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      backdrop="blur"
      classNames={{
        backdrop: "bg-black/50",
        base: "border border-gray-200",
        body: "py-6",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 items-center text-center pt-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-cornflowerblue to-blue-600 flex items-center justify-center mb-4"
          >
            <Icon icon="lucide:sparkles" className="text-white text-3xl" />
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-900">
            Get Started with {toolName}
          </h2>
          <p className="text-gray-500 font-normal text-base">
            Sign up for free to use this tool and unlock all features
          </p>
        </ModalHeader>

        <ModalBody>
          {/* Benefits */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              What you'll get with a free account:
            </p>
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                  <Icon icon={benefit.icon} className="text-cornflowerblue text-lg" />
                </div>
                <span className="text-gray-700">{benefit.text}</span>
              </motion.div>
            ))}
          </div>
        </ModalBody>

        <ModalFooter className="flex flex-col gap-3 pb-8">
          <Button
            as="a"
            href={buildAppLink('/signup')}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            size="lg"
            className="w-full bg-cornflowerblue text-white font-semibold py-6 no-underline"
          >
            Sign Up Free
            <Icon icon="lucide:arrow-right" className="ml-2" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SignUpPromptModal;
